import * as React from "react";
import apiClient from "@/api-client";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Link,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
	useCampaignPreparation,
	usePreparationAction,
	usePreparationOptions,
	type PreparationConfig,
} from "@/hooks/api/use-campaign-preparation";
import type { CampaignRequestDetail } from "@/hooks/api/use-campaign-requests";
import { campaignRequestsKeys, useTransitionCampaignRequest } from "@/hooks/api/use-campaign-requests";

import { appHref } from "@/lib/platform";

import { AccountPageLink } from "./account-page-link";
import { ProvingResults } from "./proving-results";

const empty: PreparationConfig = { agentTemplateId: "", campaignTemplateId: "", leadServiceId: "", agentServiceId: "" };
export function ProcessingTab({ request }: { request: CampaignRequestDetail }) {
	const { data, isPending, isError } = useCampaignPreparation(request.id);
	const { data: options, isError: optionsError } = usePreparationOptions(request.id);
	const action = usePreparationAction(request.id);
	const transition = useTransitionCampaignRequest();
	const client = useQueryClient();
	const [config, setConfig] = React.useState<PreparationConfig>(empty);
	const [edit, setEdit] = React.useState<{
		id: string;
		name: string;
		prompt: string;
		firstPhrase?: string;
		kind: "agent" | "post-call";
	} | null>(null);
	const [showSettings, setShowSettings] = React.useState(false);
	const served = JSON.stringify(options?.config ?? empty);
	const settingsDirty = JSON.stringify(config) !== served;
	const [rebuild, setRebuild] = React.useState(false);
	/*
	 * Re-seed whenever the *served* settings change, not once on first load. Seeding once left
	 * `settingsDirty` true forever whenever the API normalised what was saved — a different key
	 * order, or a cleared select stored as undefined rather than "" — and a permanently dirty form
	 * disables "Start preparation" and tells the operator to save settings they just saved. Keyed
	 * on the JSON, so a background refetch that changes nothing does not discard an edit in flight.
	 */
	React.useEffect(() => {
		setConfig(JSON.parse(served) as PreparationConfig);
	}, [served]);
	React.useEffect(() => {
		if (data?.run?.status === "review") {
			void client.invalidateQueries({ queryKey: campaignRequestsKeys.detail(request.id) });
			void client.invalidateQueries({ queryKey: ["campaign-preparation-options", request.id] });
		}
	}, [data?.run?.status, client, request.id]);
	const run = data?.run;
	const hasProvingRun = (data?.proving.runs.length ?? 0) > 0;
	const provingResults = (
		<ProvingResults
			id={request.id}
			accountId={options?.account.id}
			proof={data?.proving}
			approved={run?.status === "approved" && data?.proving.status !== "stale"}
		/>
	);
	const busy = action.isPending || run?.status === "queued" || run?.status === "running";
	const canReview = request.state === "in_preparation";
	const canPrepare = ["confirmed", "in_preparation"].includes(request.state) && !request.provisionedAt;
	const save = async () => {
		try {
			await action.mutateAsync({ action: "settings", data: config });
		} catch {
			/* The mutation reports the API error. */
		}
	};
	const retry = async () => {
		try {
			await action.mutateAsync({ action: "retry" });
			setRebuild(false);
		} catch {
			/* The mutation reports the API error. */
		}
	};
	return (
		<Stack spacing={3}>
			{isPending ? <CircularProgress size={24} /> : null}
			{isError || optionsError ? (
				<Alert severity="error">Preparation could not be loaded. Refresh to try again.</Alert>
			) : null}
			{request.state === "ready" ? (
				<Alert
					severity="info"
					action={
						<Button
							disabled={transition.isPending}
							onClick={() => transition.mutate({ id: request.id, state: "in_preparation" })}
						>
							Return to review
						</Button>
					}
				>
					Need a correction? Returning to review keeps the campaigns disabled and requires fresh approval and ten
					passing tests before reapplying the configuration.
				</Alert>
			) : null}
			<Typography variant="h6">Background preparation</Typography>
			<Typography color="text.secondary">
				Leads are cleaned into contact groups and call flows become private draft agents. Review the results and make
				adjustments before approving campaign creation.
			</Typography>
			{run ? (
				<Alert severity={run.status === "failed" ? "error" : run.status === "approved" ? "success" : "info"}>
					<strong>
						{
							{
								queued: "Queued",
								running: "Processing uploads",
								review: "Ready for team review",
								approved: "Admin review approved",
								failed: "Preparation needs attention",
							}[run.status]
						}
					</strong>
					{run.error ? <div>{run.error}</div> : null}
					{run.accepted === undefined ? null : (
						<div>
							{run.accepted.toLocaleString()} contacts accepted · {run.rejected?.toLocaleString() ?? 0} rows excluded
						</div>
					)}
					{run.approvedAt ? <div>Approved {new Date(run.approvedAt).toLocaleString()}</div> : null}
				</Alert>
			) : (
				<Alert severity="info">
					Processing starts when the client confirms the quote. Configure this account first.
				</Alert>
			)}
			{hasProvingRun ? provingResults : null}
			<Accordion expanded={showSettings || !options?.config} onChange={(_, expanded) => setShowSettings(expanded)}>
				<AccordionSummary expandIcon={<span>⌄</span>}>
					Account templates and LLMs · {options?.account.name ?? "this account"}
				</AccordionSummary>
				<AccordionDetails>
					<Stack spacing={2}>
						<Typography variant="subtitle1">Proving Ground suite</Typography>
						<Typography variant="body2" color="text.secondary">
							Choose one persona, exactly ten scenarios and the required scorecard. Manage their content and the judge
							model in Proving Ground.
						</Typography>
						<TextField
							select
							label="Test persona"
							value={config.provingPersonaId ?? ""}
							onChange={(e) => setConfig({ ...config, provingPersonaId: e.target.value || undefined })}
						>
							<MenuItem value="">Choose a persona</MenuItem>
							{options?.proving.personas.map((p) => (
								<MenuItem value={p.id} key={p.id}>
									{p.name}
								</MenuItem>
							))}
						</TextField>
						{(["provingScenarioIds", "provingMetricIds"] as const).map((key) => (
							<TextField
								key={key}
								select
								label={
									key === "provingScenarioIds"
										? `Test scenarios (${config[key]?.length ?? 0}/10)`
										: "Required scorecard checks"
								}
								value={config[key] ?? []}
								slotProps={{ select: { multiple: true } }}
								onChange={(e) =>
									setConfig({
										...config,
										[key]: typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value,
									})
								}
							>
								{(key === "provingScenarioIds" ? options?.proving.scenarios : options?.proving.metrics)?.map((item) => (
									<MenuItem value={item.id} key={item.id}>
										{item.name}
									</MenuItem>
								))}
							</TextField>
						))}
						<AccountPageLink accountId={options?.account.id} path="/proving-ground/runs">
							Manage scenarios, personas and scorecard
						</AccountPageLink>
						<TextField
							select
							label="Agent configuration template"
							value={config.agentTemplateId}
							onChange={(e) => setConfig({ ...config, agentTemplateId: e.target.value })}
							helperText="Copies voice, tools, flow and service settings into each new draft."
						>
							<MenuItem value="">Choose an agent</MenuItem>
							{options?.agents.map((a) => (
								<MenuItem key={a.id} value={a.id}>
									{a.name}
								</MenuItem>
							))}
						</TextField>
						<TextField
							select
							label="Campaign configuration template"
							value={config.campaignTemplateId}
							onChange={(e) => setConfig({ ...config, campaignTemplateId: e.target.value })}
							helperText="Supplies call analysis and email delivery settings. Client schedules and lead groups come from this request."
						>
							<MenuItem value="">Choose a campaign</MenuItem>
							{options?.campaigns.map((c) => (
								<MenuItem key={c.id} value={c.id}>
									{c.title}
								</MenuItem>
							))}
						</TextField>
						{(
							[
								["leadServiceId", "Lead-cleaning LLM"],
								["agentServiceId", "Agent-generation LLM"],
								["postCallServiceId", "Post-call prompt-generation LLM"],
							] as const
						).map(([key, label]) => (
							<TextField
								key={key}
								select
								label={label}
								value={config[key] ?? ""}
								onChange={(e) => setConfig({ ...config, [key]: e.target.value || undefined })}
							>
								<MenuItem value="">
									{key === "postCallServiceId" ? "Use agent-generation LLM" : "Choose a configured AI service"}
								</MenuItem>
								{options?.services.map((s) => (
									<MenuItem key={s.id} value={s.id}>
										{s.title} · {s.model || s.provider}
									</MenuItem>
								))}
							</TextField>
						))}
						<Typography variant="body2">
							Edit each prompt and its model override in{" "}
							<Link href={appHref("/platform/prompt-settings")} target="_blank" rel="noopener">
								Admin / Prompt settings
							</Link>
							. An account override takes precedence over the platform default.
						</Typography>
						<Stack direction="row" spacing={1} flexWrap="wrap">
							<Button
								variant="outlined"
								disabled={
									busy ||
									[config.agentTemplateId, config.campaignTemplateId, config.leadServiceId, config.agentServiceId].some(
										(v) => !v
									)
								}
								onClick={save}
							>
								Save account settings
							</Button>
						</Stack>
					</Stack>
				</AccordionDetails>
			</Accordion>
			<Button
				variant="contained"
				disabled={busy || settingsDirty || !canPrepare || !options?.config}
				onClick={() => (data?.agents.length ? setRebuild(true) : void retry())}
			>
				{run ? "Retry / rebuild preparation" : "Start preparation"}
			</Button>
			{settingsDirty ? (
				<Typography variant="body2" color="warning.main">
					Save the changed account settings before starting preparation.
				</Typography>
			) : null}
			{run?.models ? (
				<Stack direction="row" gap={1} flexWrap="wrap">
					{Object.entries(run.models).map(([key, m]) => (
						<Chip key={key} label={`${key}: ${m.model ?? "service default"} · ${m.scope} prompt v${m.version}`} />
					))}
				</Stack>
			) : null}
			{data?.agents.map((agent) => (
				<Stack key={agent.id} spacing={1}>
					<Typography variant="subtitle1">{agent.name}</Typography>
					{agent.conversionNotes.length > 0 ? (
						<Alert severity="warning">
							<Typography fontWeight={600}>Conversion notes — review before approval</Typography>
							<ul>
								{agent.conversionNotes.map((note, index) => (
									<li key={index}>{note}</li>
								))}
							</ul>
						</Alert>
					) : null}
					<TextField
						label="Opening message"
						value={agent.firstPhrase}
						multiline
						slotProps={{ input: { readOnly: true } }}
					/>
					<TextField
						multiline
						minRows={5}
						maxRows={12}
						value={agent.prompt}
						slotProps={{
							input: { readOnly: true },
							htmlInput: { "aria-label": `${agent.name} generated prompt` },
						}}
					/>
					<Button
						sx={{ alignSelf: "start" }}
						disabled={busy || !canReview}
						onClick={() => setEdit({ ...agent, kind: "agent" })}
					>
						Adjust draft prompt
					</Button>
					{agent.postCallPrompt ? (
						<>
							<TextField
								label="Campaign post-call analysis prompt"
								multiline
								minRows={5}
								maxRows={12}
								value={agent.postCallPrompt}
								slotProps={{ input: { readOnly: true } }}
							/>
							<Button
								sx={{ alignSelf: "start" }}
								disabled={busy || !canReview}
								onClick={() =>
									setEdit({
										id: agent.id,
										name: `${agent.name} · post-call analysis`,
										prompt: agent.postCallPrompt!,
										kind: "post-call",
									})
								}
							>
								Adjust post-call prompt
							</Button>
						</>
					) : null}
				</Stack>
			))}
			{data?.groups?.map((group) => (
				<Stack key={group.groupId} spacing={1}>
					<Typography variant="subtitle1">
						{group.market} contact group · {group.count.toLocaleString()} contacts
					</Typography>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Phone</TableCell>
								<TableCell>Prepared contact details (sample)</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{group.sample.map((lead) => (
								<TableRow key={lead.phone}>
									<TableCell>{lead.phone}</TableCell>
									<TableCell>
										{Object.entries(lead.attributes)
											.map(([key, value]) => `${key}: ${value}`)
											.join(" · ")}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Stack>
			))}
			{run?.rejected ? (
				<Button
					sx={{ alignSelf: "start" }}
					onClick={async () => {
						try {
							const response = await apiClient.get(`/campaign-requests/${request.id}/preparation/rejections`, {
								responseType: "blob",
							});
							const url = URL.createObjectURL(response.data);
							const link = document.createElement("a");
							link.href = url;
							link.download = "cleaning-report.csv";
							link.click();
							setTimeout(() => URL.revokeObjectURL(url), 1000);
						} catch {
							toast.error("Could not download the cleaning report. Please retry.");
						}
					}}
				>
					Download all excluded-row reasons
				</Button>
			) : null}
			{run?.proposedOutcomes?.length ? (
				<Stack spacing={1}>
					<Typography variant="subtitle1">Suggested call-flow outcomes</Typography>
					<Typography variant="body2" color="text.secondary">
						Compare these with the client&rsquo;s saved outcomes on the request. Update the request if needed, then
						rebuild and review.
					</Typography>
					{run.proposedOutcomes.map((o, i) => (
						<Typography key={i} variant="body2">
							<strong>{o.name}</strong>: {o.description}
							{o.isConversion ? " (conversion)" : ""}
						</Typography>
					))}
				</Stack>
			) : null}
			{run?.rejections?.length ? (
				<details>
					<summary>Review excluded rows ({run.rejected}; showing up to 1,000)</summary>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>File</TableCell>
								<TableCell>Data row</TableCell>
								<TableCell>Reason</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{run.rejections.map((r, i) => (
								<TableRow key={i}>
									<TableCell>{r.file}</TableCell>
									<TableCell>{r.row}</TableCell>
									<TableCell>{r.reason}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</details>
			) : null}
			<Button
				variant="contained"
				color="success"
				sx={{ alignSelf: "start" }}
				disabled={busy || !canReview || !["review", "approved"].includes(run?.status ?? "")}
				onClick={() => action.mutate({ action: "approve" })}
			>
				Approve and run ten proving-ground tests
			</Button>
			<Typography variant="body2" color="text.secondary">
				Assign a local caller ID for each market before approving. All ten tests per agent must pass before campaign
				creation.
			</Typography>
			{hasProvingRun ? null : provingResults}
			<Dialog open={!!edit} onClose={() => setEdit(null)} fullWidth maxWidth="md">
				<DialogTitle>Adjust {edit?.name}</DialogTitle>
				<DialogContent>
					{edit?.kind === "agent" ? (
						<TextField
							fullWidth
							multiline
							sx={{ mt: 1 }}
							label="Opening message"
							value={edit.firstPhrase ?? ""}
							onChange={(event) => setEdit({ ...edit, firstPhrase: event.target.value })}
						/>
					) : null}
					<TextField
						sx={{ mt: 1 }}
						fullWidth
						multiline
						minRows={15}
						label={edit?.kind === "post-call" ? "Post-call prompt" : "Agent prompt"}
						value={edit?.prompt ?? ""}
						onChange={(e) => setEdit(edit ? { ...edit, prompt: e.target.value } : null)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEdit(null)}>Cancel</Button>
					<Button
						disabled={
							action.isPending ||
							!edit ||
							edit.prompt.trim().length < 20 ||
							(edit.kind === "agent" && !edit.firstPhrase?.trim())
						}
						onClick={async () => {
							if (!edit) return;
							try {
								await action.mutateAsync({
									action: edit.kind,
									data: {
										agentId: edit.id,
										prompt: edit.prompt,
										...(edit.kind === "agent" ? { firstPhrase: edit.firstPhrase } : {}),
									},
								});
								setEdit(null);
							} catch {
								/* The mutation reports the API error. */
							}
						}}
					>
						Save draft
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={rebuild} onClose={() => setRebuild(false)}>
				<DialogTitle>Rebuild the draft agents?</DialogTitle>
				<DialogContent>
					This creates fresh drafts and contact groups from the current uploads and saved settings. Review any manual
					adjustments before replacing the current drafts. Approval will be cleared.
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRebuild(false)}>Keep current drafts</Button>
					<Button disabled={busy} onClick={retry}>
						Rebuild
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
}
